import React, { useEffect, useState } from "react";
import {
  adminLogin,
  adminStats,
  adminOrders,
  adminProducts,
  adminUpdateProduct,
  adminDeleteProduct,
  adminCreateProduct,
  adminGenerateMarketing,
  adminGenerateMarketingBulk,
  adminListDiscountCodes,
  adminCreateDiscountCode,
  adminDeleteDiscountCode,
  adminListCampaigns,
  adminCreateCampaign,
  adminToggleCampaign,
  adminDeleteCampaign,
  adminListReviews,
  adminUpdateReview,
  adminDeleteReview,
  adminDownloadPdfUrl,
} from "@/lib/api";
import {
  LogOut,
  BarChart3,
  Package,
  ShoppingCart,
  Sparkles,
  Trash2,
  Plus,
  Copy,
  Check,
  X,
  Tag,
  Instagram,
  Star,
  MessageSquare,
  Download,
  FileSpreadsheet,
  Zap,
} from "lucide-react";

const TABS = [
  { id: "dashboard", label: "Dashboard", icon: BarChart3 },
  { id: "products", label: "Products", icon: Package },
  { id: "orders", label: "Orders", icon: ShoppingCart },
  { id: "reviews", label: "Anmeldelser", icon: MessageSquare },
  { id: "discounts", label: "Rabattkoder", icon: Tag },
  { id: "marketing", label: "Marketing AI", icon: Sparkles },
  { id: "downloads", label: "PDF Downloads", icon: Download },
];

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("småstund_admin_token") || "");
  const [pwd, setPwd] = useState("");
  const [err, setErr] = useState("");
  const [tab, setTab] = useState("dashboard");

  const login = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      const res = await adminLogin(pwd);
      localStorage.setItem("småstund_admin_token", res.token);
      setToken(res.token);
    } catch (e) {
      setErr("Invalid password");
    }
  };

  const logout = () => {
    localStorage.removeItem("småstund_admin_token");
    setToken("");
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6] px-6">
        <form
          onSubmit={login}
          className="w-full max-w-sm bg-white border border-[#E5E2DC] rounded-2xl p-8"
        >
          <p className="text-xs uppercase tracking-[0.25em] text-[#D97B66] font-medium mb-2">
            Admin
          </p>
          <h1 className="font-heading text-2xl text-[#2C302E] mb-6">Småstund</h1>
          <input
            type="password"
            data-testid="admin-password-input"
            placeholder="Password"
            value={pwd}
            onChange={(e) => setPwd(e.target.value)}
            className="w-full bg-[#F9F8F6] border border-[#E5E2DC] rounded-xl px-4 py-3 mb-4 focus:outline-none focus:border-[#8BA896]"
          />
          {err && <p className="text-sm text-[#D97B66] mb-3" data-testid="admin-login-error">{err}</p>}
          <button
            type="submit"
            data-testid="admin-login-button"
            className="w-full bg-[#2C302E] hover:bg-[#1f221f] text-white rounded-full px-6 py-3 font-medium"
          >
            Logg inn
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <div className="border-b border-[#E5E2DC] bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-heading text-xl text-[#2C302E]">småstund</span>
            <span className="text-xs text-[#D97B66] uppercase tracking-widest">admin</span>
          </div>
          <button
            data-testid="admin-logout"
            onClick={logout}
            className="text-sm text-[#6B726C] hover:text-[#2C302E] flex items-center gap-2"
          >
            <LogOut className="w-4 h-4" strokeWidth={1.5} />
            Log out
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-8 grid lg:grid-cols-[220px_1fr] gap-8">
        <aside>
          <nav className="space-y-1">
            {TABS.map((tt) => (
              <button
                key={tt.id}
                data-testid={`admin-tab-${tt.id}`}
                onClick={() => setTab(tt.id)}
                className={`w-full text-left flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm transition-colors ${
                  tab === tt.id
                    ? "bg-[#2C302E] text-white"
                    : "text-[#2C302E] hover:bg-white"
                }`}
              >
                <tt.icon className="w-4 h-4" strokeWidth={1.5} />
                {tt.label}
              </button>
            ))}
          </nav>
        </aside>

        <main>
          {tab === "dashboard" && <Dashboard />}
          {tab === "products" && <Products />}
          {tab === "orders" && <Orders />}
          {tab === "reviews" && <Reviews />}
          {tab === "discounts" && <DiscountCodes />}
          {tab === "marketing" && <Marketing />}
          {tab === "downloads" && <DownloadsTab />}
        </main>
      </div>
    </div>
  );
}

function Dashboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    adminStats().then(setStats).catch(() => {});
  }, []);
  if (!stats) return <p className="text-[#6B726C]">Loading...</p>;
  const cards = [
    { label: "Revenue (NOK)", value: stats.total_revenue_nok.toFixed(0), accent: "#D97B66" },
    { label: "Orders", value: stats.total_orders, accent: "#8BA896" },
    { label: "Products", value: stats.products_count, accent: "#2C302E" },
    { label: "Newsletter", value: stats.newsletter_subscribers, accent: "#6B726C" },
  ];
  return (
    <div>
      <h2 className="font-heading text-3xl text-[#2C302E] mb-8">Dashboard</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {cards.map((c, i) => (
          <div
            key={i}
            data-testid={`stat-${c.label.toLowerCase().split(" ")[0]}`}
            className="bg-white border border-[#E5E2DC] rounded-2xl p-6"
          >
            <p className="text-xs uppercase tracking-widest text-[#6B726C] mb-3">{c.label}</p>
            <p
              className="font-heading text-3xl md:text-4xl font-medium"
              style={{ color: c.accent }}
            >
              {c.value}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

function Products() {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null); // product id
  const [editValues, setEditValues] = useState({});

  const load = () => adminProducts().then(setProducts);
  useEffect(() => {
    load();
  }, []);

  const startEdit = (p) => {
    setEditing(p.id);
    setEditValues({
      price_nok: p.price_nok,
      featured: p.featured,
      active: p.active,
      download_url: p.download_url || "",
    });
  };

  const save = async (id) => {
    await adminUpdateProduct(id, editValues);
    setEditing(null);
    load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await adminDeleteProduct(id);
    load();
  };

  return (
    <div>
      <h2 className="font-heading text-3xl text-[#2C302E] mb-8">Products</h2>
      <div className="bg-white border border-[#E5E2DC] rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="border-b border-[#E5E2DC] bg-[#F9F8F6]">
            <tr className="text-left">
              <th className="px-5 py-3 text-[#6B726C] font-medium">Title</th>
              <th className="px-5 py-3 text-[#6B726C] font-medium">Price</th>
              <th className="px-5 py-3 text-[#6B726C] font-medium">Featured</th>
              <th className="px-5 py-3 text-[#6B726C] font-medium">Active</th>
              <th className="px-5 py-3 text-[#6B726C] font-medium">Download URL</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => {
              const isEdit = editing === p.id;
              return (
                <tr key={p.id} className="border-t border-[#E5E2DC]">
                  <td className="px-5 py-3 text-[#2C302E]">
                    <p className="font-medium">{p.title_no}</p>
                    <p className="text-xs text-[#6B726C]">{p.slug}</p>
                  </td>
                  <td className="px-5 py-3">
                    {isEdit ? (
                      <input
                        type="number"
                        value={editValues.price_nok}
                        onChange={(e) =>
                          setEditValues({ ...editValues, price_nok: parseFloat(e.target.value) })
                        }
                        className="w-20 bg-[#F9F8F6] border border-[#E5E2DC] rounded px-2 py-1"
                      />
                    ) : (
                      `${p.price_nok} NOK`
                    )}
                  </td>
                  <td className="px-5 py-3">
                    {isEdit ? (
                      <input
                        type="checkbox"
                        checked={editValues.featured}
                        onChange={(e) =>
                          setEditValues({ ...editValues, featured: e.target.checked })
                        }
                      />
                    ) : p.featured ? (
                      "✓"
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-5 py-3">
                    {isEdit ? (
                      <input
                        type="checkbox"
                        checked={editValues.active}
                        onChange={(e) =>
                          setEditValues({ ...editValues, active: e.target.checked })
                        }
                      />
                    ) : p.active ? (
                      "✓"
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-5 py-3 max-w-xs">
                    {isEdit ? (
                      <input
                        type="url"
                        value={editValues.download_url}
                        placeholder="https://drive.google.com/..."
                        onChange={(e) =>
                          setEditValues({ ...editValues, download_url: e.target.value })
                        }
                        className="w-full bg-[#F9F8F6] border border-[#E5E2DC] rounded px-2 py-1"
                      />
                    ) : (
                      <span className="text-xs text-[#6B726C] truncate block">
                        {p.download_url || "Not set"}
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3 flex items-center gap-2 justify-end">
                    {isEdit ? (
                      <>
                        <button
                          onClick={() => save(p.id)}
                          className="bg-[#8BA896] text-white rounded-full p-1.5"
                        >
                          <Check className="w-3.5 h-3.5" strokeWidth={2} />
                        </button>
                        <button
                          onClick={() => setEditing(null)}
                          className="border border-[#E5E2DC] rounded-full p-1.5"
                        >
                          <X className="w-3.5 h-3.5" strokeWidth={2} />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => startEdit(p)}
                          data-testid={`edit-product-${p.slug}`}
                          className="text-xs text-[#2C302E] hover:text-[#D97B66] underline underline-offset-4"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => del(p.id)}
                          className="text-[#D97B66] p-1.5"
                        >
                          <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Orders() {
  const [orders, setOrders] = useState([]);
  useEffect(() => {
    adminOrders().then(setOrders).catch(() => {});
  }, []);

  return (
    <div>
      <h2 className="font-heading text-3xl text-[#2C302E] mb-8">Orders</h2>
      {orders.length === 0 ? (
        <div className="bg-white border border-[#E5E2DC] rounded-2xl p-10 text-center text-[#6B726C]">
          No orders yet. Once your first customer buys, it'll appear here.
        </div>
      ) : (
        <div className="bg-white border border-[#E5E2DC] rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="border-b border-[#E5E2DC] bg-[#F9F8F6]">
              <tr className="text-left">
                <th className="px-5 py-3 text-[#6B726C] font-medium">Date</th>
                <th className="px-5 py-3 text-[#6B726C] font-medium">Customer</th>
                <th className="px-5 py-3 text-[#6B726C] font-medium">Products</th>
                <th className="px-5 py-3 text-[#6B726C] font-medium">Amount</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} className="border-t border-[#E5E2DC]">
                  <td className="px-5 py-3 text-[#6B726C]">
                    {new Date(o.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-5 py-3 text-[#2C302E]">{o.customer_email}</td>
                  <td className="px-5 py-3 text-[#6B726C]">{o.items?.length} items</td>
                  <td className="px-5 py-3 font-medium">{o.amount_nok} NOK</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Marketing() {
  const [products, setProducts] = useState([]);
  const [slug, setSlug] = useState("");
  const [platform, setPlatform] = useState("instagram");
  const [language, setLanguage] = useState("no");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState("");
  const [copied, setCopied] = useState(false);

  // Bulk state
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkStatus, setBulkStatus] = useState("");
  const [bulkError, setBulkError] = useState("");
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);

  useEffect(() => {
    adminProducts().then((p) => {
      setProducts(p);
      if (p.length > 0) setSlug(p[0].slug);
    });
  }, []);

  const generate = async () => {
    if (!slug) return;
    setLoading(true);
    setResult("");
    try {
      const r = await adminGenerateMarketing({
        product_slug: slug,
        platform,
        language,
      });
      setResult(r.posts);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const isTimeout = e?.code === "ECONNABORTED";
      if (isTimeout) {
        setResult(
          "⏱️ Tok for lang tid å generere. Vennligst prøv igjen — det fungerer som regel ved andre forsøk."
        );
      } else if (detail) {
        setResult(`❌ ${detail}`);
      } else {
        setResult("❌ Generation failed. Try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const generateAll = async () => {
    setShowBulkConfirm(false);
    setBulkLoading(true);
    setBulkError("");
    setBulkStatus(
      "Genererer for alle produkter… Dette kan ta 5-10 minutter. Hold fanen åpen."
    );
    try {
      const res = await adminGenerateMarketingBulk({ platform, language });
      // Trigger CSV download
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const today = new Date().toISOString().slice(0, 10);
      const a = document.createElement("a");
      a.href = url;
      a.download = `smaastund-${platform}-${today}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      const total = res.headers["x-total-products"];
      const failed = res.headers["x-failed-count"];
      setBulkStatus(
        `✅ Ferdig! Lastet ned CSV med ${total} produkter${
          failed && failed !== "0" ? ` (${failed} feilet)` : ""
        }.`
      );
    } catch (e) {
      const isTimeout = e?.code === "ECONNABORTED";
      setBulkError(
        isTimeout
          ? "⏱️ Tok for lang tid. Vennligst prøv på nytt — eller generer ett produkt om gangen."
          : "❌ Kunne ikke generere bulk. Vennligst prøv igjen."
      );
      setBulkStatus("");
    } finally {
      setBulkLoading(false);
    }
  };

  const copy = () => {
    navigator.clipboard.writeText(result);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div>
      <h2 className="font-heading text-3xl text-[#2C302E] mb-2">Marketing AI</h2>
      <p className="text-[#6B726C] mb-8">
        Generate ready-to-post social media content for your products.
      </p>

      <div className="bg-white border border-[#E5E2DC] rounded-2xl p-6 md:p-8 space-y-4">
        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-[#6B726C]">Product</label>
            <select
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              data-testid="marketing-product-select"
              className="mt-2 w-full bg-[#F9F8F6] border border-[#E5E2DC] rounded-xl px-3 py-2.5 focus:outline-none"
            >
              {products.map((p) => (
                <option key={p.slug} value={p.slug}>
                  {p.title_no}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[#6B726C]">Platform</label>
            <select
              value={platform}
              onChange={(e) => setPlatform(e.target.value)}
              className="mt-2 w-full bg-[#F9F8F6] border border-[#E5E2DC] rounded-xl px-3 py-2.5"
            >
              <option value="instagram">Instagram</option>
              <option value="tiktok">TikTok</option>
              <option value="facebook">Facebook</option>
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[#6B726C]">Language</label>
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="mt-2 w-full bg-[#F9F8F6] border border-[#E5E2DC] rounded-xl px-3 py-2.5"
            >
              <option value="no">Norsk</option>
              <option value="en">English</option>
            </select>
          </div>
        </div>

        <button
          data-testid="marketing-generate-button"
          onClick={generate}
          disabled={loading}
          className="bg-[#D97B66] hover:bg-[#C66A55] disabled:opacity-60 text-white rounded-full px-7 py-2.5 font-medium flex items-center gap-2"
        >
          <Sparkles className="w-4 h-4" strokeWidth={1.5} />
          {loading ? "Generating..." : "Generate posts"}
        </button>

        {result && (
          <div className="mt-6 relative">
            <button
              onClick={copy}
              className="absolute top-3 right-3 text-xs bg-white border border-[#E5E2DC] rounded-full px-3 py-1.5 flex items-center gap-1"
            >
              {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
              {copied ? "Copied!" : "Copy"}
            </button>
            <pre
              data-testid="marketing-result"
              className="bg-[#F9F8F6] border border-[#E5E2DC] rounded-xl p-5 pr-20 text-sm text-[#2C302E] whitespace-pre-wrap font-body leading-relaxed"
            >
              {result}
            </pre>
          </div>
        )}
      </div>

      {/* Bulk generation card */}
      <div className="mt-8 bg-white border border-[#E5E2DC] rounded-2xl p-6 md:p-8">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="bg-[#EFF5EE] rounded-2xl p-3">
            <FileSpreadsheet className="w-6 h-6 text-[#5A7F60]" strokeWidth={1.5} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-heading text-xl text-[#2C302E] font-medium">
              Generer for alle produkter
            </h3>
            <p className="text-sm text-[#6B726C] mt-1 leading-relaxed">
              Lag 3 innlegg per produkt for {products.length} produkter, og last ned
              som CSV-fil. Tar ca. 5-10 minutter — hold fanen åpen til det er ferdig.
            </p>
            <p className="text-xs text-[#6B726C] mt-2">
              💰 Bruker AI-kreditt: ~$0.50 totalt per kjøring
            </p>
          </div>
          {!showBulkConfirm && !bulkLoading && (
            <button
              data-testid="marketing-bulk-button"
              onClick={() => setShowBulkConfirm(true)}
              className="bg-[#2C302E] hover:bg-[#1f221f] text-white rounded-full px-6 py-2.5 font-medium flex items-center gap-2"
            >
              <Download className="w-4 h-4" strokeWidth={1.5} />
              Generer alle &amp; last ned CSV
            </button>
          )}
        </div>

        {showBulkConfirm && (
          <div className="mt-5 bg-[#FCE6E3] border border-[#E8A09A]/40 rounded-xl p-5">
            <p className="text-sm text-[#2C302E] font-medium mb-3">
              Er du sikker? Dette vil generere innlegg for alle {products.length}{" "}
              produktene og bruke AI-kreditt (~$0.50).
            </p>
            <div className="flex gap-2 flex-wrap">
              <button
                data-testid="marketing-bulk-confirm"
                onClick={generateAll}
                className="bg-[#D97B66] hover:bg-[#C66A55] text-white rounded-full px-5 py-2 text-sm font-medium"
              >
                Ja, generer alle
              </button>
              <button
                onClick={() => setShowBulkConfirm(false)}
                className="bg-white border border-[#E5E2DC] hover:border-[#2C302E]/40 text-[#2C302E] rounded-full px-5 py-2 text-sm font-medium"
              >
                Avbryt
              </button>
            </div>
          </div>
        )}

        {bulkLoading && (
          <div className="mt-5 bg-[#F9F8F6] border border-[#E5E2DC] rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-2 h-2 bg-[#D97B66] rounded-full animate-pulse"></div>
              <div
                className="w-2 h-2 bg-[#D97B66] rounded-full animate-pulse"
                style={{ animationDelay: "0.2s" }}
              ></div>
              <div
                className="w-2 h-2 bg-[#D97B66] rounded-full animate-pulse"
                style={{ animationDelay: "0.4s" }}
              ></div>
              <span className="text-sm text-[#2C302E] font-medium">
                Genererer innlegg…
              </span>
            </div>
            <p className="text-sm text-[#6B726C]">{bulkStatus}</p>
          </div>
        )}

        {!bulkLoading && bulkStatus && (
          <p
            data-testid="marketing-bulk-status"
            className="mt-5 text-sm text-[#5A7F60] bg-[#EFF5EE] border border-[#8BA896]/30 rounded-xl px-4 py-3"
          >
            {bulkStatus}
          </p>
        )}

        {bulkError && (
          <p
            data-testid="marketing-bulk-error"
            className="mt-5 text-sm text-[#D97B66] bg-[#FBE9E5] border border-[#D97B66]/30 rounded-xl px-4 py-3"
          >
            {bulkError}
          </p>
        )}
      </div>
    </div>
  );
}


function DiscountCodes() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // Create form
  const [code, setCode] = useState("");
  const [percent, setPercent] = useState(10);
  const [validUntil, setValidUntil] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() + 1);
    return d.toISOString().slice(0, 10);
  });
  const [creating, setCreating] = useState(false);

  // Instagram copy
  const [copiedId, setCopiedId] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminListDiscountCodes();
      setCodes(data);
    } catch (e) {
      setErr("Kunne ikke laste rabattkoder");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    setErr("");
    setCreating(true);
    try {
      await adminCreateDiscountCode({
        code: code.trim().toUpperCase(),
        discount_percent: Number(percent),
        valid_until: validUntil,
      });
      setCode("");
      setPercent(10);
      load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Kunne ikke opprette koden");
    } finally {
      setCreating(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Slette denne rabattkoden?")) return;
    await adminDeleteDiscountCode(id);
    load();
  };

  const formatDateNo = (iso) => {
    const [y, m, d] = iso.split("-");
    return `${d}.${m}.${y}`;
  };

  const buildInstagramPost = (c) => {
    return [
      "🌸 Spesialtilbud hos Småstund!",
      "",
      `Bruk koden ${c.code} og få ${c.discount_percent}% rabatt`,
      "på alle våre digitale ressurser for barn og foreldre.",
      "",
      `Gyldig til ${formatDateNo(c.valid_until)} ✨`,
      "",
      "👉 smaastund.no",
      "",
      "#småstund #norskefamilier #foreldretips #barneoppdragelse #digitalresurs",
    ].join("\n");
  };

  const copyPost = (c) => {
    const text = buildInstagramPost(c);
    navigator.clipboard.writeText(text);
    setCopiedId(c.id);
    setTimeout(() => setCopiedId(""), 2000);
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div>
      <h2 className="font-heading text-3xl text-[#2C302E] mb-8">Rabattkoder</h2>

      {/* Launch Campaign Card */}
      <CampaignManager />

      {/* Create form */}
      <div className="bg-white border border-[#E5E2DC] rounded-2xl p-6 mb-8">
        <h3 className="font-heading text-lg text-[#2C302E] mb-4">
          Opprett ny rabattkode
        </h3>
        <form
          onSubmit={create}
          className="grid grid-cols-1 md:grid-cols-[2fr_1fr_1.5fr_auto] gap-3 items-end"
        >
          <div>
            <label className="text-xs uppercase tracking-widest text-[#6B726C] font-medium">
              Kode
            </label>
            <input
              type="text"
              required
              data-testid="discount-code-name"
              value={code}
              onChange={(e) =>
                setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))
              }
              placeholder="SOMMER10"
              maxLength={20}
              className="mt-1.5 w-full bg-[#F9F8F6] border border-[#E5E2DC] rounded-xl px-4 py-2.5 uppercase tracking-wider focus:outline-none focus:border-[#8BA896]"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[#6B726C] font-medium">
              Prosent
            </label>
            <input
              type="number"
              required
              min={1}
              max={100}
              data-testid="discount-percent-input"
              value={percent}
              onChange={(e) => setPercent(e.target.value)}
              className="mt-1.5 w-full bg-[#F9F8F6] border border-[#E5E2DC] rounded-xl px-4 py-2.5 focus:outline-none focus:border-[#8BA896]"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[#6B726C] font-medium">
              Gyldig til
            </label>
            <input
              type="date"
              required
              data-testid="discount-valid-until"
              value={validUntil}
              min={today}
              onChange={(e) => setValidUntil(e.target.value)}
              className="mt-1.5 w-full bg-[#F9F8F6] border border-[#E5E2DC] rounded-xl px-4 py-2.5 focus:outline-none focus:border-[#8BA896]"
            />
          </div>
          <button
            type="submit"
            data-testid="discount-create-btn"
            disabled={creating}
            className="bg-[#D97B66] hover:bg-[#C66A55] disabled:opacity-60 text-white rounded-xl px-6 py-2.5 font-medium flex items-center gap-2 justify-center"
          >
            <Plus className="w-4 h-4" strokeWidth={2} />
            {creating ? "Lager..." : "Opprett"}
          </button>
        </form>
        {err && (
          <p className="text-sm text-[#D97B66] mt-3" data-testid="discount-create-error">
            {err}
          </p>
        )}
      </div>

      {/* List */}
      {loading ? (
        <p className="text-[#6B726C]">Laster...</p>
      ) : codes.length === 0 ? (
        <div className="bg-white border border-dashed border-[#E5E2DC] rounded-2xl p-10 text-center">
          <Tag className="w-8 h-8 text-[#8BA896] mx-auto mb-3" strokeWidth={1.2} />
          <p className="text-[#6B726C]">
            Ingen rabattkoder enda. Opprett din første over!
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {codes.map((c) => {
            const expired = c.valid_until < today;
            return (
              <div
                key={c.id}
                data-testid={`discount-row-${c.code}`}
                className={`bg-white border rounded-2xl p-5 flex flex-col md:flex-row md:items-center gap-4 ${
                  expired ? "border-[#E5E2DC] opacity-60" : "border-[#E5E2DC]"
                }`}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-heading text-2xl text-[#2C302E] tracking-wider">
                      {c.code}
                    </span>
                    <span className="bg-[#EFF5EE] text-[#5A7F60] text-xs font-medium rounded-full px-2.5 py-0.5">
                      −{c.discount_percent}%
                    </span>
                    {expired && (
                      <span className="bg-[#FBE9E5] text-[#D97B66] text-xs font-medium rounded-full px-2.5 py-0.5">
                        Utløpt
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-[#6B726C] mt-1">
                    Gyldig til {formatDateNo(c.valid_until)} • Brukt {c.times_used}{" "}
                    ganger
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => copyPost(c)}
                    data-testid={`discount-share-${c.code}`}
                    className="inline-flex items-center gap-2 bg-[#2C302E] hover:bg-[#1f221f] text-white rounded-full px-4 py-2 text-sm"
                  >
                    {copiedId === c.id ? (
                      <Check className="w-3.5 h-3.5" strokeWidth={2} />
                    ) : (
                      <Instagram className="w-3.5 h-3.5" strokeWidth={2} />
                    )}
                    {copiedId === c.id ? "Kopiert!" : "Kopier Instagram-tekst"}
                  </button>
                  <button
                    onClick={() => remove(c.id)}
                    data-testid={`discount-delete-${c.code}`}
                    className="p-2 text-[#6B726C] hover:text-[#D97B66] border border-[#E5E2DC] rounded-full"
                    aria-label="Slett"
                  >
                    <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StarRow({ value }) {
  return (
    <div className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className="w-3.5 h-3.5"
          fill={n <= value ? "#E8B547" : "transparent"}
          stroke="#E8B547"
          strokeWidth={1.6}
        />
      ))}
    </div>
  );
}

function Reviews() {
  const [filter, setFilter] = useState("pending");
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminListReviews(filter === "all" ? undefined : filter);
      setReviews(data);
    } catch (e) {
      setReviews([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const setStatus = async (id, status) => {
    await adminUpdateReview(id, status);
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Slette denne anmeldelsen?")) return;
    await adminDeleteReview(id);
    load();
  };

  const filters = [
    { id: "pending", label: "Venter på godkjenning" },
    { id: "approved", label: "Godkjente" },
    { id: "rejected", label: "Avviste" },
    { id: "all", label: "Alle" },
  ];

  return (
    <div>
      <h2 className="font-heading text-3xl text-[#2C302E] mb-8">Anmeldelser</h2>

      <div className="flex gap-2 mb-6 flex-wrap">
        {filters.map((f) => (
          <button
            key={f.id}
            data-testid={`reviews-filter-${f.id}`}
            onClick={() => setFilter(f.id)}
            className={`text-sm rounded-full px-4 py-1.5 transition-colors ${
              filter === f.id
                ? "bg-[#2C302E] text-white"
                : "bg-white border border-[#E5E2DC] text-[#2C302E] hover:border-[#2C302E]/40"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-[#6B726C]">Laster...</p>
      ) : reviews.length === 0 ? (
        <div className="bg-white border border-dashed border-[#E5E2DC] rounded-2xl p-10 text-center">
          <MessageSquare className="w-8 h-8 text-[#8BA896] mx-auto mb-3" strokeWidth={1.2} />
          <p className="text-[#6B726C]">Ingen anmeldelser i denne kategorien enda.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {reviews.map((r) => {
            const badge =
              r.status === "pending"
                ? { bg: "#FFF4E5", color: "#B8770E", label: "Venter" }
                : r.status === "approved"
                ? { bg: "#EFF5EE", color: "#5A7F60", label: "Godkjent" }
                : { bg: "#FBE9E5", color: "#D97B66", label: "Avvist" };
            return (
              <div
                key={r.id}
                data-testid={`admin-review-${r.id}`}
                className="bg-white border border-[#E5E2DC] rounded-2xl p-5"
              >
                <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
                  <div>
                    <div className="flex items-center gap-3 flex-wrap mb-1">
                      <StarRow value={r.stars} />
                      <span
                        className="text-[10px] uppercase tracking-widest font-semibold rounded-full px-2 py-0.5"
                        style={{ backgroundColor: badge.bg, color: badge.color }}
                      >
                        {badge.label}
                      </span>
                    </div>
                    <p className="font-heading text-base text-[#2C302E] font-medium">
                      {r.customer_name}{" "}
                      <span className="text-xs text-[#6B726C] font-normal">
                        · {r.customer_email}
                      </span>
                    </p>
                    <p className="text-xs text-[#6B726C] mt-1">
                      Produkt: <span className="text-[#2C302E]">{r.product_slug}</span> ·{" "}
                      {new Date(r.created_at).toLocaleDateString("nb-NO")}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {r.status !== "approved" && (
                      <button
                        onClick={() => setStatus(r.id, "approved")}
                        data-testid={`review-approve-${r.id}`}
                        className="bg-[#8BA896] hover:bg-[#759783] text-white rounded-full px-4 py-1.5 text-sm inline-flex items-center gap-1.5"
                      >
                        <Check className="w-3.5 h-3.5" strokeWidth={2} />
                        Godkjenn
                      </button>
                    )}
                    {r.status !== "rejected" && (
                      <button
                        onClick={() => setStatus(r.id, "rejected")}
                        data-testid={`review-reject-${r.id}`}
                        className="bg-white border border-[#E5E2DC] hover:border-[#D97B66] text-[#2C302E] hover:text-[#D97B66] rounded-full px-4 py-1.5 text-sm inline-flex items-center gap-1.5"
                      >
                        <X className="w-3.5 h-3.5" strokeWidth={2} />
                        Avvis
                      </button>
                    )}
                    <button
                      onClick={() => remove(r.id)}
                      data-testid={`review-delete-${r.id}`}
                      className="p-1.5 text-[#6B726C] hover:text-[#D97B66] border border-[#E5E2DC] rounded-full"
                      aria-label="Slett"
                    >
                      <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </button>
                  </div>
                </div>
                {r.comment && (
                  <p className="text-sm text-[#4A4F4A] leading-relaxed whitespace-pre-line bg-[#F9F8F6] rounded-xl px-4 py-3 mt-3">
                    {r.comment}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


function CampaignManager() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [titleNo, setTitleNo] = useState("Lansering!");
  const [messageNo, setMessageNo] = useState(
    "Få 25% rabatt på alt — kun de første 48 timer"
  );
  const [percent, setPercent] = useState(25);
  const [hours, setHours] = useState(48);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminListCampaigns();
      setCampaigns(data);
    } catch (e) {
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    try {
      await adminCreateCampaign({
        title_no: titleNo,
        title_en: titleNo,
        message_no: messageNo,
        message_en: messageNo,
        discount_percent: Number(percent),
        hours_duration: Number(hours),
        code_prefix: "LANSERING",
      });
      setShowForm(false);
      load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Kunne ikke opprette kampanjen");
    } finally {
      setSubmitting(false);
    }
  };

  const toggle = async (c) => {
    await adminToggleCampaign(c.id, !c.active);
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Slette denne kampanjen?")) return;
    await adminDeleteCampaign(id);
    load();
  };

  const now = new Date();
  const isLive = (c) =>
    c.active && new Date(c.start_at) <= now && new Date(c.end_at) > now;

  const fmt = (iso) => new Date(iso).toLocaleString("nb-NO");

  return (
    <div className="bg-white border border-[#E5E2DC] rounded-2xl p-6 mb-8">
      <div className="flex items-start gap-4 mb-5 flex-wrap">
        <div className="bg-[#FCE6E3] rounded-2xl p-3">
          <Zap className="w-6 h-6 text-[#D97B66]" strokeWidth={1.5} fill="#D97B66" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-heading text-xl text-[#2C302E] font-medium">
            Launch Kampanje
          </h3>
          <p className="text-sm text-[#6B726C] mt-1 leading-relaxed">
            Vis en banner med nedtellingstimer på toppen av butikken. Lager
            automatisk en rabattkode som kunder kan bruke med ett klikk.
          </p>
        </div>
        {!showForm && !loading && (
          <button
            data-testid="campaign-new-btn"
            onClick={() => setShowForm(true)}
            className="bg-[#D97B66] hover:bg-[#C66A55] text-white rounded-full px-5 py-2 text-sm font-medium flex items-center gap-2"
          >
            <Plus className="w-4 h-4" strokeWidth={2} />
            Ny kampanje
          </button>
        )}
      </div>

      {showForm && (
        <form
          onSubmit={create}
          className="bg-[#F9F8F6] border border-[#E5E2DC] rounded-xl p-5 space-y-4 mb-5"
        >
          <div>
            <label className="text-xs uppercase tracking-widest text-[#6B726C] font-medium">
              Tittel
            </label>
            <input
              data-testid="campaign-title-input"
              type="text"
              required
              value={titleNo}
              onChange={(e) => setTitleNo(e.target.value)}
              className="mt-1.5 w-full bg-white border border-[#E5E2DC] rounded-xl px-4 py-2.5 focus:outline-none focus:border-[#8BA896]"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[#6B726C] font-medium">
              Melding til kunder
            </label>
            <input
              data-testid="campaign-message-input"
              type="text"
              required
              value={messageNo}
              onChange={(e) => setMessageNo(e.target.value)}
              className="mt-1.5 w-full bg-white border border-[#E5E2DC] rounded-xl px-4 py-2.5 focus:outline-none focus:border-[#8BA896]"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-widest text-[#6B726C] font-medium">
                Prosent (%)
              </label>
              <input
                type="number"
                required
                min={1}
                max={100}
                value={percent}
                onChange={(e) => setPercent(e.target.value)}
                className="mt-1.5 w-full bg-white border border-[#E5E2DC] rounded-xl px-4 py-2.5 focus:outline-none focus:border-[#8BA896]"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#6B726C] font-medium">
                Varighet (timer)
              </label>
              <input
                type="number"
                required
                min={1}
                max={720}
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                className="mt-1.5 w-full bg-white border border-[#E5E2DC] rounded-xl px-4 py-2.5 focus:outline-none focus:border-[#8BA896]"
              />
            </div>
          </div>
          {err && <p className="text-sm text-[#D97B66]">{err}</p>}
          <div className="flex gap-2 pt-1">
            <button
              type="submit"
              data-testid="campaign-create-btn"
              disabled={submitting}
              className="bg-[#D97B66] hover:bg-[#C66A55] disabled:opacity-60 text-white rounded-full px-5 py-2 text-sm font-medium"
            >
              {submitting ? "Lager..." : "Lansér kampanje"}
            </button>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="bg-white border border-[#E5E2DC] hover:border-[#2C302E]/40 text-[#2C302E] rounded-full px-5 py-2 text-sm font-medium"
            >
              Avbryt
            </button>
          </div>
        </form>
      )}

      {!loading && campaigns.length > 0 && (
        <div className="space-y-2">
          {campaigns.map((c) => {
            const live = isLive(c);
            return (
              <div
                key={c.id}
                data-testid={`campaign-row-${c.id}`}
                className={`border rounded-xl p-4 flex items-center gap-4 flex-wrap ${
                  live ? "bg-[#FCE6E3] border-[#E8A09A]/40" : "bg-white border-[#E5E2DC]"
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-heading text-base font-medium text-[#2C302E]">
                      {c.title_no}
                    </span>
                    {live && (
                      <span className="text-[10px] uppercase tracking-widest font-semibold bg-[#D97B66] text-white rounded-full px-2 py-0.5">
                        Live nå
                      </span>
                    )}
                    {!c.active && (
                      <span className="text-[10px] uppercase tracking-widest text-[#6B726C] bg-[#F9F8F6] rounded-full px-2 py-0.5">
                        Av
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-[#6B726C]">
                    Kode <span className="font-mono font-semibold text-[#2C302E]">{c.discount_code}</span> ({c.discount_percent}%) ·{" "}
                    {fmt(c.start_at)} → {fmt(c.end_at)}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => toggle(c)}
                    data-testid={`campaign-toggle-${c.id}`}
                    className={`rounded-full px-4 py-1.5 text-sm font-medium ${
                      c.active
                        ? "bg-white border border-[#E5E2DC] hover:border-[#D97B66] text-[#2C302E]"
                        : "bg-[#8BA896] hover:bg-[#759783] text-white"
                    }`}
                  >
                    {c.active ? "Skru av" : "Skru på"}
                  </button>
                  <button
                    onClick={() => remove(c.id)}
                    data-testid={`campaign-delete-${c.id}`}
                    className="p-1.5 text-[#6B726C] hover:text-[#D97B66] border border-[#E5E2DC] rounded-full"
                  >
                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


function DownloadsTab() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    adminProducts()
      .then((data) => {
        const sorted = [...(data || [])].sort((a, b) => {
          const ca = (a.category || "").localeCompare(b.category || "");
          if (ca !== 0) return ca;
          return (a.title_no || "").localeCompare(b.title_no || "");
        });
        setProducts(sorted);
      })
      .finally(() => setLoading(false));
  }, []);

  const visible = products.filter((p) => {
    if (!filter.trim()) return true;
    const q = filter.toLowerCase();
    return (
      (p.title_no || "").toLowerCase().includes(q) ||
      (p.slug || "").toLowerCase().includes(q) ||
      (p.category || "").toLowerCase().includes(q)
    );
  });

  return (
    <div data-testid="admin-downloads-tab">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-3xl text-[#2C302E] mb-2">
            PDF Downloads
          </h2>
          <p className="text-sm text-[#6B726C] max-w-xl">
            Last ned PDF-en for hvilket som helst produkt — bypasser kjøp-tokenet.
            Kun for testing av at filene er korrekt generert og leveres.
          </p>
        </div>
        <input
          type="text"
          placeholder="Søk på tittel, slug eller kategori…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="px-4 py-2 border border-[#E5E2DC] rounded-full text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#D97B66] w-full sm:w-80"
          data-testid="admin-downloads-search"
        />
      </div>

      {loading ? (
        <p className="text-[#6B726C]">Laster produkter…</p>
      ) : (
        <div className="bg-white rounded-2xl border border-[#E5E2DC] overflow-hidden">
          <div className="px-4 py-3 bg-[#F9F8F6] text-xs uppercase tracking-wider text-[#6B726C] font-semibold flex items-center justify-between">
            <span>
              {visible.length} av {products.length} produkter
            </span>
            <span>Klikk for å laste ned PDF</span>
          </div>
          <ul className="divide-y divide-[#E5E2DC]">
            {visible.map((p) => (
              <li
                key={p.id || p.slug}
                className="flex items-center gap-4 px-4 py-3 hover:bg-[#FAF9F6] transition-colors"
                data-testid={`admin-download-row-${p.slug}`}
              >
                <img
                  src={p.image_url}
                  alt=""
                  className="w-12 h-12 rounded-lg object-cover bg-[#F9F8F6] flex-shrink-0"
                  loading="lazy"
                />
                <div className="flex-1 min-w-0">
                  <p
                    className="font-medium text-[#2C302E] truncate"
                    data-testid={`admin-download-title-${p.slug}`}
                  >
                    {p.title_no}
                  </p>
                  <p className="text-xs text-[#6B726C] truncate">
                    {p.slug} · {p.category} · {p.pages || "?"} sider · {p.price_nok} NOK
                  </p>
                </div>
                <a
                  href={adminDownloadPdfUrl(p.slug)}
                  download
                  className="inline-flex items-center gap-2 bg-[#2C302E] text-white px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider hover:bg-[#D97B66] transition-colors whitespace-nowrap"
                  data-testid={`admin-download-button-${p.slug}`}
                >
                  <Download size={14} />
                  Last ned PDF
                </a>
              </li>
            ))}
            {visible.length === 0 && (
              <li className="px-4 py-12 text-center text-sm text-[#6B726C]">
                Ingen produkter matcher søket.
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

